import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { registerObject } from "go-form";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { apiOrganization } from "../../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ProductApi,
	PurchaseCorrectionApi,
	TaxApi,
} from "../../../../../../../../../../../../../services/Api/Organization/types";
import ChangeProductModal from "../../../../../../../components/ChangeProductModal";
import PurchaseCorrectionFormItemsTableBody from "./PurchaseCorrectionFormItemsTableBody";
import PurchaseCorrectionFormItemsTableHeader from "./PurchaseCorrectionFormItemsTableHeader";

interface Props {
	form: UseFormReturn<PurchaseCorrectionApi>;
	taxes: TaxApi[];
}

const PurchaseCorrectionFormItemsTable = ({ form, taxes }: Props) => {
	const isMobile = useWindowSize().isMobile;
	const { t } = useTranslation();
	const [showChangeProductModal, setShowChangeProductModal] = useState<number | null>(null);
	const { setValue, register } = form;

	const handleChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		setValue(`items.${index}.id`, null);

		const tax = taxes.find(({ id }) => id === product.tax_id);
		registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id"]);
		setValue(`items.${index}.product`, product);
		setValue(`items.${index}.product_id`, product.id, { shouldDirty: true });
		setValue(`items.${index}.volume`, product?.default_volume ?? null, { shouldDirty: true });
		registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
		setValue(`items.${index}.tax`, tax ?? null);
		setValue(`items.${index}.tax_id`, product?.tax_id ?? null, { shouldDirty: true });

		const volumeInput: HTMLInputElement | null = document.querySelector(`input[name='items.${index}.volume']`);
		if (volumeInput) {
			volumeInput.focus();
		}
	};

	const handleConfirmChangeProduct = async (product: ProductApi) => {
		if (showChangeProductModal !== null) {
			await handleChangeProduct(product, showChangeProductModal);
			setShowChangeProductModal(null);
		}
	};

	const searchProducts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"productType|e": `BASIC${FILTER_VALUE_SEPARATOR}COMPOSITE`,
			include: "volumes",
		};
		return apiOrganization.getProductSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	return (
		<>
			<fieldset className="form-group">
				<h5 className={isMobile ? "mb-0" : ""}>{t("common.word.items")}</h5>
				<div className={`${isMobile ? "table-responsive" : ""}`}>
					<table
						className={`table table-form positions-table purchase-corrections-table ${
							isMobile ? "vertical-top" : ""
						}`}
					>
						<PurchaseCorrectionFormItemsTableHeader />
						<PurchaseCorrectionFormItemsTableBody
							form={form}
							taxes={taxes}
							handleChangeProduct={handleChangeProduct}
							setShowChangeProductModal={setShowChangeProductModal}
							searchProducts={searchProducts}
						/>
					</table>
				</div>
			</fieldset>
			{showChangeProductModal !== null && (
				<ChangeProductModal
					isShown={showChangeProductModal !== null}
					onHide={() => setShowChangeProductModal(null)}
					onConfirm={handleConfirmChangeProduct}
					onSearchProducts={searchProducts}
					index={showChangeProductModal}
					form={form}
					onParseOptionValue={(option) => option.label}
				/>
			)}
		</>
	);
};

export default PurchaseCorrectionFormItemsTable;
